import { taskIsDue, type CadenceVisit } from './cadence.ts';
import { z } from 'zod';
import { SERVICE_FREQUENCIES } from '../constants/index.ts';
import { isoDateSchema, localizedTextSchema, requiredText } from '../schemas/common.ts';
import { dateTimeFormat } from '../schemas/formats.ts';

export const VISIT_TYPES = ['standard', 'deep_clean', 'turnover', 'common_area', 'touch_up', 'custom'] as const;
export const CHECKLIST_KINDS = ['residential', 'deep', 'str_addon', 'commercial', 'custom'] as const;
export type ChecklistVisitType = (typeof VISIT_TYPES)[number];
const key = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);
export const checklistDayRuleSchema = z.object({
  weekdays: z.array(z.number().int().min(0).max(6)).max(7).default([]),
  everyNthVisit: z.number().int().min(1).max(365).nullable().default(null),
  frequency: z.enum(SERVICE_FREQUENCIES).default('every_visit'),
});
export const checklistStackTaskSchema = z.object({
  id: key,
  name: requiredText(200, 'Enter a task name'),
  nameTranslations: localizedTextSchema(200).default(null),
  description: z.string().trim().max(2000).nullable().default(null),
  descriptionTranslations: localizedTextSchema(2000).default(null),
  estimatedMinutes: z.number().int().min(0).max(1440).default(0),
  requiresPhoto: z.boolean().default(false),
  rule: checklistDayRuleSchema.default({ weekdays: [], everyNthVisit: null, frequency: 'every_visit' }),
});
export const checklistSectionSchema = z.object({
  id: key,
  name: requiredText(200, 'Enter a section name'),
  nameTranslations: localizedTextSchema(200).default(null),
  rule: checklistDayRuleSchema.default({ weekdays: [], everyNthVisit: null, frequency: 'every_visit' }),
  tasks: z.array(checklistStackTaskSchema).min(1).max(300),
});
export const checklistDefinitionSchema = z
  .object({
    name: requiredText(120, 'Enter a checklist name'),
    nameTranslations: localizedTextSchema(120).default(null),
    kind: z.enum(CHECKLIST_KINDS).default('custom'),
    sections: z.array(checklistSectionSchema).min(1).max(50),
  })
  .superRefine((value, ctx) => {
    const sectionIds = new Set<string>();
    const taskIds = new Set<string>();
    let count = 0;
    for (const section of value.sections) {
      if (sectionIds.has(section.id)) ctx.addIssue({ code: 'custom', message: 'Section IDs must be unique' });
      sectionIds.add(section.id);
      for (const task of section.tasks) {
        if (taskIds.has(task.id)) ctx.addIssue({ code: 'custom', message: 'Task IDs must be unique' });
        taskIds.add(task.id);
        count++;
      }
    }
    if (count > 300) ctx.addIssue({ code: 'custom', message: 'A checklist may contain at most 300 tasks' });
  });
export const checklistLayerSchema = z
  .object({
    id: key,
    templateId: key.nullable().default(null),
    visitType: z.enum(VISIT_TYPES).nullable().default(null),
    definition: checklistDefinitionSchema.nullable().default(null),
  })
  .refine((layer) => !(layer.templateId && layer.definition), { message: 'Choose a template or property tasks' });
const requestId = z.uuid();
export const saveChecklistTemplateSchema = z.object({
  requestId,
  version: z.number().int().positive().optional(),
  definition: checklistDefinitionSchema,
});
export const saveChecklistStackSchema = z
  .object({
    requestId,
    version: z.number().int().min(0),
    layers: z.array(checklistLayerSchema).max(20),
  })
  .superRefine((value, ctx) => {
    if (new Set(value.layers.map((layer) => layer.id)).size !== value.layers.length)
      ctx.addIssue({ code: 'custom', message: 'Layer IDs must be unique' });
  });
export const editVisitChecklistSchema = z.object({
  expectedStartsAt: z.iso.datetime(dateTimeFormat({ offset: true })),
  expectedOccurrenceDate: isoDateSchema,
  requestId,
  revision: z.number().int().positive(),
  scope: z.enum(['this_visit', 'this_and_upcoming']),
  visitType: z.enum(VISIT_TYPES).nullable(),
  definition: checklistDefinitionSchema,
});
export type ChecklistDayRule = z.infer<typeof checklistDayRuleSchema>;
export type ChecklistDefinition = z.infer<typeof checklistDefinitionSchema>;
export type ChecklistLayer = z.infer<typeof checklistLayerSchema>;
export interface ChecklistTemplate {
  id: string;
  locationId: string | null;
  version: number;
  definition: ChecklistDefinition;
}
export interface ChecklistStack {
  version: number;
  layers: ChecklistLayer[];
}

/** Sunday is zero. Rules combine, and occurrence one is the first visit. */
export function checklistDayIsDue(rule: ChecklistDayRule, date: string, ordinal: number): boolean {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return (
    (!rule.weekdays.length || rule.weekdays.includes(day)) &&
    (rule.everyNthVisit === null || ordinal % rule.everyNthVisit === 0)
  );
}

export interface ChecklistRuleVisit extends CadenceVisit {
  ordinal: number;
}
/** Frequency chooses the first visit that meets both section and task day rules. */
export function checklistRulesAreDue(
  rules: readonly ChecklistDayRule[],
  visit: ChecklistRuleVisit,
  seriesVisits: readonly ChecklistRuleVisit[],
): boolean {
  const eligible = (candidate: ChecklistRuleVisit) =>
    rules.every((rule) => checklistDayIsDue(rule, candidate.scheduledDate, candidate.ordinal));
  if (!eligible(visit)) return false;
  const candidates = seriesVisits.filter(eligible);
  return rules.every((rule) => taskIsDue(rule.frequency, visit, candidates));
}
